import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import { UserProfile } from "Domain/User/Entity/UserProfile";
import Section from "pages/components/Section";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useTranslation } from "react-i18next";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";
import { getUrl, DEBTOR_DETAIL } from "Infrastructure/config/routesConfig";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import PhoneIcon from "@mui/icons-material/Phone";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";

type DisplayDebtorProps = {
  debtor: UserProfile;
  update?: boolean;
};

export default function DisplayDebtor({ debtor, update }: DisplayDebtorProps) {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const civility = t(
    `FORM.OPTIONS.${debtor.gender === "m" ? "male" : "female"}`
  );

  const goToDebtorProfile = update
    ? {}
    : {
        icon: <EditIcon />,
        iconAction: () => {
          navigate(getUrl(DEBTOR_DETAIL, { id: debtor.publicId }));
        },
      };

  return (
    <Section title={"debtor_infos"} {...goToDebtorProfile}>
      <Box marginTop={-1}>
        <Typography
          variant="h6"
          textAlign={"center"}
          fontWeight={"bold"}
          marginBottom={1}
        >
          {`${civility} ${debtor.firstName} ${debtor.lastName}`}
        </Typography>

        <TableContainer>
          <Table
            sx={{
              tableLayout: "fixed",
              width: "100%",
              "& tbody tr:last-child td": {
                borderBottom: "none",
              },
            }}
            aria-label="simple table"
          >
            <TableBody>
              {[
                address(debtor, t),
                email(debtor),
                mobile(debtor),
                phone(debtor),
                bank(debtor),
                externalIdentifier(debtor),
              ].map((row: { key: any; value: any }, i) => (
                <Row key={i} left={row.key} value={row.value}></Row>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Section>
  );
}

function address(debtor: UserProfile, t: Function) {
  return {
    key: <LocationOnIcon />,
    value: (
      <>
        <Typography variant="body2">
          {" "}
          {debtor.boxNumber} {debtor.address}
        </Typography>
        <Typography variant="body2">
          {debtor.postCode} {debtor.city}, {t(`COUNTRIES.${debtor.country}`)}
        </Typography>
        {debtor.box && (
          <Typography variant="body2">
            {t(`FORM.FIELD.box`)}: {debtor.box}
          </Typography>
        )}
      </>
    ),
  };
}

function email(debtor: UserProfile) {
  return {
    key: <AlternateEmailIcon />,
    value: (
      <Typography
        variant="body2"
        sx={{
          wordWrap: "break-word",
        }}
      >
        {debtor.email}
      </Typography>
    ),
  };
}
function mobile(debtor: UserProfile) {
  return {
    key: <SmartphoneIcon />,
    value: debtor.mobile,
  };
}
function phone(debtor: UserProfile) {
  return {
    key: <PhoneIcon />,
    value: debtor.phone,
  };
}
function bank(debtor: UserProfile) {
  return {
    key: <AccountBalanceIcon />,
    value: debtor.iban,
  };
}
function externalIdentifier(debtor: UserProfile) {
  return {
    key: debtor.externalIdentifierType,
    value: debtor.externalIdentifier,
  };
}

function Row({ left, value }: any) {
  return (
    <TableRow
      sx={{
        width: "100%",
        paddingTop: 1,
        paddingBottom: 1,
        overflow: "clip",
      }}
    >
      <TableCell
        size="small"
        style={{
          height: "auto",
          padding: "10px 0",
          width: "40px",
          overflow: "clip",
          fontWeight: "bold",
        }}
        align="left"
      >
        <Box display={"flex"} alignItems={"center"}>
          {left}
        </Box>
      </TableCell>
      <TableCell
        size="small"
        style={{
          padding: 0,
          overflow: "clip",
        }}
        align="left"
      >
        {value}
      </TableCell>
    </TableRow>
  );
}
